/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (DEFINITIVE FONT FIX) ---
 * - MISSION: To standardize font loading for the entire application,
 * resolving inconsistencies in text rendering.
 *
 * - THE BUG: The project had conflicting font-loading strategies
 * (manual @font-face rules in CSS vs. @fontsource packages), causing the
 * browser to fall back to system fonts and creating visually incorrect
 * appearances.
 *
 * - THE DEFINITIVE FIX:
 * 1.  STANDARDIZED IMPORTS: This file now acts as the single source of
 * truth for fonts. It imports the specific weights needed for 'Inter'
 * and 'Montserrat' from the installed @fontsource packages.
 * 2.  CSS IMPORT: It continues to import the main `index.css` file,
 * which will be cleaned up in the next step.
 *
 * - RESULT: This change establishes a clean, modern, and efficient
 * font-loading strategy. The fonts will now be bundled with the
 * application's JavaScript, eliminating CSS conflicts and ensuring the
 * correct typography is displayed consistently across the entire site.
 */

// --- CRITICAL POLYFILLS FOR LEGACY BROWSERS (Safari < 10, Firefox < 50) ---
import 'regenerator-runtime/runtime'; // Essential for async/await in legacy builds
// import 'text-encoding'; // REMOVED: Handled by vite-plugin-legacy / unneeded for modern browsers
// import 'whatwg-fetch'; // REMOVED: Handled by vite-plugin-legacy / unneeded for modern browsers
// import 'regenerator-runtime/runtime'; // REMOVED: Handled by vite-plugin-legacy
// import 'text-encoding'; // REMOVED: Handled by vite-plugin-legacy
// import 'whatwg-fetch'; // REMOVED: Handled by vite-plugin-legacy
// import 'intersection-observer'; // REMOVED: Handled by vite-plugin-legacy
// import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'; // REMOVED: Handled by vite-plugin-legacy
// import ResizeObserver from 'resize-observer-polyfill'; // REMOVED: Handled by vite-plugin-legacy
import 'proxy-polyfill'; // KEEP: Critical for Safari 9/10 Proxy crashes (e.g. usage in data binding)

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './i18n';

// --- DEFINITIVE FONT FIX: Fonts are now loaded via Google Fonts CDN in index.html ---
// This prevents render-blocking CSS in the main bundle.
// See index.html for the non-blocking <link> tags.

// --- REVERTING INLINE CSS: Standard Import for Performance ---
// Importing CSS normally prompts Vite to emit a standard <link rel="stylesheet">.
// Inlining it via JS (previous code) caused "Forced Reflows" (FOUC + Layout Thrashing)
// and increased Main Thread blocking time, contributing to the "Document Latency" error.
import './index.css';

// --- Deployment Safety Net: Chunk Error Auto-Recovery ---
// When a new build is deployed, old chunk hashes are removed from the server.
// Users with open tabs still reference old hashes via cached index.html.
// Vite fires 'vite:preloadError' when a dynamic import fails to preload.
// We auto-reload ONCE to fetch the new index.html with correct chunk hashes.
// sessionStorage guard (wrapped in try-catch for Safari private mode)
// prevents infinite reload loops if the error persists.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  try {
    const RELOAD_KEY = 'vite_preload_error_reloaded';
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    const COOLDOWN_MS = 10000; // 10-second guard window

    if (!lastReload || (now - parseInt(lastReload, 10)) > COOLDOWN_MS) {
      sessionStorage.setItem(RELOAD_KEY, now.toString());
      window.location.reload();
    }
  } catch (_e) {
    // sessionStorage unavailable (Safari private mode, iOS WebView)
    // Fall through — user will see the error boundary instead of infinite reload
  }
});

const renderApp = () => {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

// --- Check for missing Modern APIs (e.g. Safari 11-13) ---
const needsPolyfills =
  !window.ResizeObserver ||
  !window.IntersectionObserver ||
  !Promise.allSettled ||
  !Array.prototype.flat ||
  !Object.fromEntries ||
  !window.globalThis ||
  typeof structuredClone === 'undefined';

if (needsPolyfills) {
  console.log('Detected legacy browser capabilities. Loading compatibility fixes...');
  import('./polyfills/legacy-fixes.js')
    .then(() => {
      renderApp();
    })
    .catch((err) => {
      console.error('Failed to load polyfills:', err);
      // Attempt to render anyway
      renderApp();
    });
} else {
  renderApp();
}
